<script setup>
import IconArrow07 from '@/components/svg/IconArrow07.vue'

const props = defineProps({
  guideConfig: {
    type: Object,
    required: true,
  },
})

const guideOpenState = ref(
  props.guideConfig.sections.map((_, i) => i === 0),
)

const toggleSection = (index) => {
  guideOpenState.value[index] = !guideOpenState.value[index]
}

const headerStyle = computed(() => {
  const bg = props.guideConfig.headerBg
  if (bg.startsWith('linear-gradient')) {
    return { background: bg }
  }
  return { backgroundColor: bg }
})
</script>

<template>
  <div
    class="ticket-product-guide"
    :class="guideConfig.rootClass"
    :style="{ '--guide-accent': guideConfig.accentColor }"
  >
    <div
      v-for="(section, index) in guideConfig.sections"
      :key="index"
      class="guide-section"
      :class="{ open: guideOpenState[index] }"
    >
      <div
        class="guide-header"
        :style="headerStyle"
        @click="toggleSection(index)"
      >
        <div
          class="header-content"
          :class="{ 'header-content--center': section.titleHtml }"
        >
          <span
            v-if="section.titleHtml"
            class="ticket-title-html"
            v-html="section.titleHtml"
          />
          <template v-else>
            <span class="ticket-type">{{ section.type }}</span>
            <span v-if="section.discountRate" class="discount-rate">
              {{ section.discountRate }}
            </span>
            <span v-if="section.desc" class="ticket-desc">{{ section.desc }}</span>
          </template>
        </div>
        <div class="header-right">
          <IconArrow07 class="arrow-icon" />
        </div>
      </div>
      <div v-show="guideOpenState[index]" class="guide-body" v-html="section.bodyHtml" />
    </div>
  </div>
</template>

<style lang="scss">
.ticket-product-guide {
  display: none;
  width: 100%;
  padding: 20px 16px;
  background: #fff;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
  }

  .guide-section {
    &:last-child {
      border-bottom: 1px solid #ebebeb;
    }
  }

  .guide-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 12px;
    border-top: 1px solid #ebebeb;
    border-bottom: 1px solid #ebebeb;
    cursor: pointer;

    .header-content {
      display: flex;
      align-items: center;
      gap: 24px;

      &--center {
        flex: 1;
        justify-content: center;
        gap: 0;
      }
    }

    .ticket-title-html {
      color: #333;
      font-family: Pretendard;
      font-size: 15px;
      font-weight: 700;
      line-height: 1.3;
      text-align: center;
    }

    .header-right {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .ticket-type {
      flex-shrink: 0;
      min-width: 70px;
      color: #333;
      font-family: Pretendard;
      font-size: 15px;
      font-weight: 700;
      line-height: 20px;
    }

    .ticket-desc {
      color: #333;
      font-family: Pretendard;
      font-size: 13px;
      font-weight: 500;
      line-height: 20px;
    }

    .discount-rate {
      color: var(--guide-accent);
      font-family: Pretendard;
      font-size: 15px;
      font-weight: 700;
      line-height: 1.3;
    }

    .arrow-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      transition: transform 0.2s ease;
      transform: rotate(180deg);
    }
  }

  .guide-section.open .guide-header .arrow-icon {
    transform: rotate(0deg);
  }

  .accent-text {
    color: var(--guide-accent, #eb4c10);
    font-weight: 700;
  }

  &.guide-yongsin-harmony-package {
    .guide-body .body-title {
      font-size: 15px;
      line-height: 1.5;
    }
  }

  .guide-body {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 33px 40px;
    gap: 15px;
    background: #fff;

    .body-top,
    .body-bottom {
      display: flex;
      flex-direction: column;
      align-items: center;
      text-align: center;
    }

    .body-top {
      gap: 5px;
    }

    .body-bottom {
      gap: 2px;
    }

    .body-title {
      color: #333;
      font-family: Pretendard;
      font-size: 14px;
      font-weight: 700;
      line-height: 20px;
      margin: 0;
    }

    .body-desc {
      color: #333;
      font-family: Pretendard;
      font-size: 13px;
      font-weight: 500;
      line-height: 20px;
      margin: 0;
    }

    .body-highlight {
      display: flex;
      align-items: center;
      gap: 5px;
      color: #333;
      font-family: Pretendard;
      font-size: 14px;
      font-weight: 700;
      line-height: 20px;
      margin: 0;
    }

    .discount-badge {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 1px 8px;
      background: #eb4c10;
      border-radius: 100px;
      color: #fff;
      font-family: Pretendard;
      font-size: 15px;
      font-weight: 700;
      line-height: 20px;
    }

    .accent-text {
      color: var(--guide-accent, #eb4c10);
      font-weight: 700;
    }
  }
}
</style>
